import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal, SidebarModalListItem,
} from '@domain-group/suit-theme'

import { toggleArray } from '@domain-group/javascript-utils'

import { getTaskTypeSetting, subTaskTypes } from '@functions/taskListItem'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import EntitySelector from '@components/EntitySelector'

import useBulkAssignUserTasksModal from './hooks/useBulkAssignUserTasksModal'

const MODAL_KEY = 'BulkAssignUserTasksModal'

type ModalCallbacks = {
  bulkAssignUserTasks: (payload: HandleCallbackActionParams) => void,
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  requestOptions?: object,
  showModal: boolean,
}

const BulkAssignUserTasksModal = (props: ModalProps) => {
  const { callbacks, requestOptions, showModal } = props
  const { bulkAssignUserTasks, closeModal } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] ?? {}
  const { user } = modalPayload

  const assignTasksPayload = useBulkAssignUserTasksModal({
    user,
  })

  const {
    callbacks: {
      bulkAssignUserTasks: bulkAssignFn,
    },
    formPayload: {
      entityState,
      entityState: { limit, task_types, user_id },
      handlers,
      saveEnabled,
      setEntityState,
    },
    updating,
  } = assignTasksPayload

  const bulkAssignTasksPayload = {
    callbacks: {
      action: bulkAssignFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions,
    toastText: 'Tasks Assigned',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Assign" titleSecondLine="Tasks" />

      <SidebarModal.Content>
        <Form>
          <EntitySelector
            // @ts-expect-error TS2741
            callbacks={{
              getSearchEntities: entities => entities.map((entity) => {
                const { id, full_name } = entity
                return { ...entity, full_name: `#${id} - ${full_name}` }
              }),
              selectItem: user => setEntityState({ user_id: user?.id || '' }),
            }}
            label="Assignee"
            entityKey="users"
            entityTitleKey="full_name"
            selectedItemId={user_id}
            showEntityId
          />

          <Form.Field label="Task Types" marginTop="large">
            {subTaskTypes.map((subTaskType) => {
              // @ts-expect-error TS2345
              const label = getTaskTypeSetting(subTaskType, 'label')
              return (
                <SidebarModalListItem
                  hideAngleIcon
                  isChecked={task_types.includes(subTaskType)}
                  key={subTaskType}
                  onClick={() => setEntityState({ task_types: toggleArray(task_types, subTaskType) })}
                  showCheckbox
                  title={label}
                />
              )
            })}
          </Form.Field>

          <Form.Field label="Limit" marginTop="medium">
            <input type="number" name="limit" min="1" max="10" value={limit} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          // @ts-expect-error TS2345
          onClick={() => bulkAssignUserTasks(bulkAssignTasksPayload)}
          size="large"
        >
          Assign Tasks
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <BulkAssignUserTasksModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
